import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { loginRequest } from "config/auth-config";
import { msalInstance } from "config/msal-instance";

let redirectingToLogin = false;

const isSpaRefreshTokenExpired = (error: unknown) => {
  const anyErr = error as any;
  const code = anyErr?.errorCode ?? anyErr?.error ?? anyErr?.code;
  const sub = anyErr?.subError ?? anyErr?.suberror;
  return code === "invalid_grant" && sub === "bad_token";
};

const REDIRECT_IN_PROGRESS_ERROR = "Login redirect is already in progress";

const redirectToLogin = async (): Promise<never> => {
  await msalInstance.acquireTokenRedirect(loginRequest);
  throw new Error(REDIRECT_IN_PROGRESS_ERROR);
};

/**
 * Returns an access token if it can be acquired silently.
 * If user interaction is required, triggers redirect flow and aborts current request flow.
 */
export const getAccessTokenOrRedirect = async (): Promise<string> => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    if (redirectingToLogin) {
      throw new Error(REDIRECT_IN_PROGRESS_ERROR);
    }

    redirectingToLogin = true;
    return redirectToLogin();
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    const needsInteraction =
      error instanceof InteractionRequiredAuthError || isSpaRefreshTokenExpired(error);

    if (needsInteraction && !redirectingToLogin) {
      redirectingToLogin = true;

      try {
        await msalInstance.clearCache({ account: msalInstance.getActiveAccount() });
      } catch {
        // ignore
      }

      return redirectToLogin();
    }

    if (needsInteraction) {
      throw new Error(REDIRECT_IN_PROGRESS_ERROR);
    }

    throw error instanceof Error ? error : new Error(String(error));
  }
};
